import Banner from '@/assets/banner/background.webp';
import PageBanner from '@/components/banner/PageBanner';
import { ContainerFluid } from '@/components/container/Container';
import SidebarPopularNews from '@/components/news/SidebarPopularNews';
import ProtectImage from '@/components/protect/ProtectImage';
import Seo from '@/components/seo/Seo';
import ShareButtons from '@/components/share/ShareButton';
import NewsDetailSkeleton from '@/components/skeleton/NewsDetailSkeleton';
import { BodySm, BodySmHTML, BtnTextSm, H4 } from '@/components/ui/typography/Typography';
import { ApiService } from '@/services/api.services';
import { formatDateIndo } from '@/utils/formatDate';
import { formatViews } from '@/utils/formatViews';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { IoArrowBack } from 'react-icons/io5';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

export default function NewsDetailPage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDetail() {
      setLoading(true);
      try {
        const res = await ApiService.getNewsBySlug(slug);
        setData(res?.data || null);
        setPrev(res?.prev || null);
        setNext(res?.next || null);
      } catch (err) {
        console.error(err);
        setData(null);
      } finally {
        setLoading(false);
      }
    }

    if (slug) fetchDetail();
  }, [slug]);

  if (!loading && !data) return <Navigate to="/404" replace />;

  const description = data?.excerpt || data?.content?.replace(/<[^>]+>/g, '').slice(0, 155);

  const canonical = `https://www.abdikertaraharja.co.id/berita/${slug}`;
  return (
    <>
      {!loading && data && (
        <Seo
          title={`${data.title} | Berita Abdi Kerta Raharja`}
          description={description}
          canonical={canonical}
        />
      )}
      <div>
        {/* Page Banner */}
        <PageBanner
          image={Banner}
          loading={loading}
          title={data?.title}
          description={
            data
              ? `${data.category?.name} • ${formatDateIndo(
                  data.created_at
                )} • dilihat ${formatViews(data.views)} kali`
              : null
          }
        />

        <ContainerFluid className="pb-4 lg:pb-16">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="group flex items-center gap-2 w-fit bg-white border border-gray-200 px-4 py-2 my-4 rounded-md lg:rounded-xl shadow-sm text-gray-700 hover:text-primary hover:border-primary/40 transition"
          >
            <IoArrowBack size={20} />
            <BtnTextSm>Kembali</BtnTextSm>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            {/* MAIN */}
            <div className="col-span-12 lg:col-span-9">
              {loading ? (
                <NewsDetailSkeleton />
              ) : (
                <motion.div
                  key={slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="bg-white rounded-2xl"
                >
                  <ProtectImage
                    src={data.thumbnail}
                    alt={data.title}
                    className="w-full aspect-[16/9] rounded-md lg:rounded-xl"
                  />

                  <div className="py-6 space-y-4">
                    <H4 className="font-semibold leading-snug">{data.title}</H4>
                    <BodySmHTML content={data.content} />
                  </div>

                  {/* Share */}
                  <ShareButtons title={data.title} slug={data.slug} content={data.content} />

                  <div className="mt-14 pt-10 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* PREV — TOP (mobile) | LEFT (desktop) */}
                      <div className="w-full">
                        {prev && (
                          <Link
                            to={`/berita/${prev.slug}`}
                            className="
                w-full h-full
                flex items-center gap-4
                p-6
                border rounded-2xl
                hover:border-accent-500 hover:bg-accent-50
                transition
              "
                          >
                            <ChevronLeft className="w-6 h-6 text-gray-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500 mb-1">Berita Sebelumnya</p>
                              <BodySm className="font-semibold line-clamp-2">{prev.title}</BodySm>
                            </div>
                          </Link>
                        )}
                      </div>

                      {/* NEXT — BOTTOM (mobile) | RIGHT (desktop) */}
                      <div className="w-full md:flex md:justify-end">
                        {next && (
                          <Link
                            to={`/berita/${next.slug}`}
                            className="
                w-full h-full
                flex items-center justify-end gap-4
                p-6
                border rounded-2xl
                hover:border-accent-500 hover:bg-accent-50
                transition text-right
              "
                          >
                            <div className="min-w-0">
                              <p className="text-xs text-gray-500 mb-1">Berita Selanjutnya</p>
                              <BodySm className="font-semibold line-clamp-2">{next.title}</BodySm>
                            </div>
                            <ChevronRight className="w-6 h-6 text-gray-400 shrink-0" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* SIDEBAR */}
            <div className="col-span-12 lg:col-span-3 border-t lg:border-t-0 border-gray-200 py-4 lg:py-0">
              <SidebarPopularNews refreshKey={slug} />
            </div>
          </div>
        </ContainerFluid>
      </div>
    </>
  );
}
